import type { WarehouseStorage } from './types';

/**
 * Capacity form model shared by the Storage form (one BIN) and the bulk
 * dialog (every BIN under a section / location): six text fields (a number
 * typed by hand, "" = empty) plus whether m³ was typed by the user.
 *
 * m³ derives from the dimensions (`w × d × h / 1e6`, 3 decimals) until the
 * user edits m³ by hand; clearing m³ hands it back to the dimensions.
 */

export const CAPACITY_KEYS = [
  'widthCm',
  'depthCm',
  'heightCm',
  'capacityCbm',
  'capacityKg',
  'capacityQty',
] as const;

export type CapacityKey = (typeof CAPACITY_KEYS)[number];

export interface CapacityDraft {
  widthCm: string;
  depthCm: string;
  heightCm: string;
  capacityCbm: string;
  capacityKg: string;
  capacityQty: string;
  /** m³ was typed (or loaded as a value that is not the derived one). */
  cbmTouched: boolean;
}

export type CapacityValues = Record<CapacityKey, number | null>;
export type CapacityErrors = Partial<Record<CapacityKey, string>>;

export const EMPTY_CAPACITY: CapacityDraft = {
  widthCm: '',
  depthCm: '',
  heightCm: '',
  capacityCbm: '',
  capacityKg: '',
  capacityQty: '',
  cbmTouched: false,
};

export const CAPACITY_LABEL: Record<CapacityKey, string> = {
  widthCm: 'Lebar',
  depthCm: 'Dalam',
  heightCm: 'Tinggi',
  capacityCbm: 'Kapasitas m³',
  capacityKg: 'Kapasitas kg',
  capacityQty: 'Kapasitas qty',
};

const text = (v: number | null | undefined): string =>
  v === null || v === undefined ? '' : String(v);

/** `1.5` / `"1,5"` / `""` → number, null (empty) or NaN (not a number). */
export const parseAmount = (s: string): number | null => {
  const t = s.trim().replace(',', '.');
  if (!t) return null;
  return /^\d*\.?\d+$|^\d+\.$/.test(t) ? Number(t) : Number.NaN;
};

const round3 = (n: number): number => Math.round(n * 1000) / 1000;

/** `w × d × h / 1e6` as the text for the m³ field; null until all three are > 0. */
export const deriveCbm = (w: string, d: string, h: string): string | null => {
  const [a, b, c] = [w, d, h].map(parseAmount);
  if (!a || !b || !c || [a, b, c].some(Number.isNaN)) return null;
  return String(round3(((a as number) * (b as number) * (c as number)) / 1e6));
};

/** The draft a storage opens with (empty draft for none / not a BIN). */
export const capacityDraftOf = (
  s?: Partial<WarehouseStorage> | null
): CapacityDraft => {
  const d: CapacityDraft = {
    widthCm: text(s?.widthCm),
    depthCm: text(s?.depthCm),
    heightCm: text(s?.heightCm),
    capacityCbm: text(s?.capacityCbm),
    capacityKg: text(s?.capacityKg),
    capacityQty: text(s?.capacityQty),
    cbmTouched: false,
  };
  // An m³ ops corrected by hand must not be overwritten by the dimensions.
  d.cbmTouched =
    d.capacityCbm !== '' &&
    d.capacityCbm !== deriveCbm(d.widthCm, d.depthCm, d.heightCm);
  return d;
};

/** Type `value` into one field, keeping m³ in step with the dimensions. */
export const setCapacityField = (
  d: CapacityDraft,
  key: CapacityKey,
  value: string
): CapacityDraft => {
  const next: CapacityDraft = { ...d, [key]: value };
  if (key === 'capacityCbm') next.cbmTouched = value !== '';
  if (
    !next.cbmTouched &&
    (key === 'widthCm' || key === 'depthCm' || key === 'heightCm')
  )
    next.capacityCbm =
      deriveCbm(next.widthCm, next.depthCm, next.heightCm) ?? '';
  return next;
};

/** Every field as number | null, with a message for each one that is not ≥ 0. */
export const parseCapacity = (
  d: CapacityDraft
): { values: CapacityValues; errors: CapacityErrors } => {
  const values = {} as CapacityValues;
  const errors: CapacityErrors = {};
  CAPACITY_KEYS.forEach((k) => {
    const n = parseAmount(d[k]);
    if (n !== null && (Number.isNaN(n) || n < 0))
      errors[k] = `${CAPACITY_LABEL[k]} harus berupa angka ≥ 0.`;
    values[k] = n !== null && !Number.isNaN(n) && n >= 0 ? n : null;
  });
  return { values, errors };
};

/** The fields of `values` that `keep` accepts (a PATCH / PUT body). */
export const pickCapacity = (
  values: CapacityValues,
  keep: (key: CapacityKey) => boolean
): Partial<CapacityValues> => {
  const out: Partial<CapacityValues> = {};
  CAPACITY_KEYS.forEach((k) => {
    if (keep(k)) out[k] = values[k];
  });
  return out;
};
